import * as React from 'react'

import { NetworkLogo } from 'main/components/interface'
import { RatingDisplay, WSVerifiedIcon } from 'main/components/school/Callout/common'
import { useWhitelabel } from 'main/framework'
import { SchoolListingType } from 'main/gql/globalTypes'
import { schoolListingPagePath } from 'main/routes'
import { Link, Small, SmartImage } from 'main/ui'
import { formatAge } from 'main/util/age'
import { AssetName } from 'main/util/asset'
import { formatCurrencyRange } from 'main/util/currency'
import { profileEnrollmentPricing } from 'main/util/profile'
import { determineImage } from 'main/util/school'
import { formatTime } from 'main/util/time'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../../../common'
import { i18nKeyPrefix } from '../../../i18n'
import { RelatedPrograms_schoolListings_listings } from '../../gql'
import {
  Container,
  DetailContainer,
  PhotoContainer,
  photoDimensions,
  ProfileContainer,
  TitleContainer,
} from './Callout.styles'

const PRE = `${i18nKeyPrefix}-RELATED-PROGRAMS-CALLOUT`

interface IProps {
  listing: RelatedPrograms_schoolListings_listings
  networkLogo?: string
}

export default function Callout(props: IProps) {
  const { listing } = props
  const { name, aggregateRatings } = listing
  const profileData = makeProfileData(listing)
  const whitelabel = useWhitelabel()
  const networkLogo = whitelabel.getAsset(AssetName.LOGO_SMALL)
  const imageUrl = determineImage(
    listing.type === SchoolListingType.INTERNAL,
    listing.schoolId || listing.externalSchoolId,
    whitelabel,
    listing.imageUrl,
  )

  const { t } = useTranslation(LOCIZE_NAMESPACE)

  return (
    <Container>
      <Link to={schoolListingPagePath(listing)} rel={listing.schoolId ? '' : 'nofollow'}>
        <TitleContainer>
          {listing.type === SchoolListingType.INTERNAL && renderVerifiedIcon()}
          {name}
        </TitleContainer>
        <DetailContainer>
          <PhotoContainer>
            {imageUrl && (
              <SmartImage
                height={photoDimensions.height}
                width={photoDimensions.width}
                src={`${imageUrl}/`}
                critical
              />
            )}
          </PhotoContainer>
          <ProfileContainer>
            {profileData && profileData.pricing && (
              <Small strong>
                {t(`${PRE}-PRICING`, {
                  range: formatCurrencyRange(profileData.pricing.min, profileData.pricing.max),
                })}
              </Small>
            )}

            {profileData && profileData.ages && (
              <Small>
                {formatAge(profileData.ages.min)} - {formatAge(profileData.ages.max)}
              </Small>
            )}

            {profileData && profileData.hours && (
              <Small>
                {formatTime(profileData.hours.start)} - {formatTime(profileData.hours.end)}
              </Small>
            )}

            {aggregateRatings && aggregateRatings.overall && (
              <RatingDisplay
                value={aggregateRatings.overall}
                count={aggregateRatings.count}
                size={13}
              />
            )}
          </ProfileContainer>
        </DetailContainer>
      </Link>
    </Container>
  )

  function renderVerifiedIcon() {
    if (networkLogo) return <NetworkLogo src={networkLogo} size={15} />

    return <WSVerifiedIcon />
  }
}

function makeProfileData(listing: RelatedPrograms_schoolListings_listings) {
  const { profile } = listing
  if (!profile) return null

  const { ageMonthsMin, ageMonthsMax } = profile

  const ages =
    ageMonthsMin !== null && ageMonthsMax !== null ? { min: ageMonthsMin, max: ageMonthsMax } : null

  const { hoursStart, hoursEnd } = profile
  const hours = hoursStart && hoursEnd ? { start: hoursStart, end: hoursEnd } : null

  const { enrollment } = profile
  const pricing = enrollment ? profileEnrollmentPricing(enrollment) : null

  return { ages, hours, pricing }
}
